"use client";

import { useEffect, useState } from "react";
import { API_BASE } from "../_lib/api";

// Forum-session auth for the PUBLIC surface. Deliberately NOT Supabase auth —
// "Sign in with InsuraCentral" (OIDC via the platform's own provider) returns
// a backend-minted forum JWT. No Supabase session is created on this origin,
// so the CRM's single-session machinery can never be touched from here.

const TOKEN_KEY = "ic-forum-session";
const USER_KEY = "ic-forum-user";
const OAUTH_ISSUER = (process.env.NEXT_PUBLIC_IC_OAUTH_ISSUER || `${API_BASE}/oauth`).replace(/\/+$/, "");
const CLIENT_ID = process.env.NEXT_PUBLIC_FORUM_OAUTH_CLIENT_ID || "insuracentral-forum";
export const WRITES_ENABLED = process.env.NEXT_PUBLIC_FORUM_WRITES_ENABLED !== "false";

export interface ForumSessionUser {
  id: string;
  name: string;
  avatarUrl: string | null;
  // Public profile handle; null until the account has posted or replied.
  handle?: string | null;
}

export function getForumToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function getForumUser(): ForumSessionUser | null {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}
export function forumSignOut() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
  window.location.reload();
}

const b64url = (buf: ArrayBuffer | Uint8Array) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// PKCE start: stash verifier+state+nonce, bounce to the IC OAuth server.
// NO prompt=login → an agent already signed into the CRM in this browser
// gets silent one-click SSO.
export async function startForumLogin(returnTo?: string) {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  const nonce = b64url(crypto.getRandomValues(new Uint8Array(16)));
  const challenge = b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  sessionStorage.setItem("forum-pkce", JSON.stringify({ verifier, state, nonce, returnTo: returnTo || window.location.pathname }));

  const redirectUri = `${window.location.origin}/auth/callback`;
  const url = new URL(`${OAUTH_ISSUER}/auth`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  window.location.assign(url.toString());
}

// Callback completion: swap code (+PKCE verifier) for the forum session JWT
// via the backend (the client secret lives ONLY server-side).
export async function completeForumLogin(): Promise<{ ok: boolean; returnTo: string; error?: string; code?: string }> {
  const params = new URLSearchParams(window.location.search);
  const saved = JSON.parse(sessionStorage.getItem("forum-pkce") || "null");
  sessionStorage.removeItem("forum-pkce");
  const code = params.get("code");
  const state = params.get("state");
  if (params.get("error")) {
    return { ok: false, returnTo: saved?.returnTo || "/", error: params.get("error_description") || params.get("error")! };
  }
  if (!code || !saved || saved.state !== state) {
    return { ok: false, returnTo: saved?.returnTo || "/", error: "Sign-in state mismatch — please try again" };
  }
  try {
    const res = await fetch(`${API_BASE}/api/v1/public/forum/auth/callback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        codeVerifier: saved.verifier,
        redirectUri: `${window.location.origin}/auth/callback`,
        nonce: saved.nonce,
      }),
    });
    const json = await res.json();
    const data = json?.data?.data || json?.data || json;
    if (!res.ok || !data?.token) {
      // The backend refuses a session outright for enterprise-hierarchy and
      // inactive accounts (403 with a reason code) — surface it as such.
      return { ok: false, returnTo: saved.returnTo || "/", error: data?.message || json?.message || "Sign-in failed", code: data?.error || json?.error || undefined };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return { ok: true, returnTo: saved.returnTo || "/" };
  } catch {
    return { ok: false, returnTo: saved?.returnTo || "/", error: "Sign-in failed — network error" };
  }
}

// Masthead corner: "Sign in" when anonymous, name + sign-out when signed in.
// The single "Join free" call to action lives in the masthead itself.
export function AuthCorner() {
  const [user, setUser] = useState<ForumSessionUser | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setUser(getForumUser());
    setMounted(true);
  }, []);

  if (user) {
    return (
      <div className="flex items-center gap-2">
        {user.avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={user.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
        ) : (
          <span className="fp-mono flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-[11px] font-semibold text-blue-700">
            {user.name?.[0] || "A"}
          </span>
        )}
        <span className="hidden text-[13px] font-medium text-slate-800 sm:block">{user.name}</span>
        <button onClick={forumSignOut} className="fp-inline text-[12px] text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline">
          Sign out
        </button>
      </div>
    );
  }
  if (!WRITES_ENABLED) return null;
  return (
    <>
      <button
        onClick={() => startForumLogin()}
        className={`h-9 rounded-md px-3 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50${mounted ? "" : " fp-guest"}`}
      >
        Sign in
      </button>
      {!mounted && <span aria-hidden className="fp-member-ph inline-block h-7 w-7 rounded-full bg-slate-200" />}
    </>
  );
}
